"use client";

import TitleWithIcon from "@components/molecules/TitleWithIcon";
import Text from "@components/atoms/Text";
import Button from "@components/atoms/Button";
import styles from "./OnRoadPriceSection.module.scss";
import { formatCurrencyINR, maskAmount } from "@utils/numberFormatter";
import assetLoader from "@utils/internalImageLoder";
import CitySelectionModal from "../CitySelectionModal";
import { useEffect, useState } from "react";
import { addCaptureIntentIfNeeded, getLoginUrl } from "@utils/getLoginUrl";
import { PageType } from "@constants/pageTypes";
import { EventType, trackEventWithCommonProps } from "@utils/eventTracker";
import { getCurrencyWithLakh } from "@utils/stringFormatters";
import { useRouter } from "next/navigation";
import { setSelectedCityClient } from "@helpers/client/getSelectedCity";
import { useCity } from "@context/CityContext";
import { City } from "../CitySelectionModal/City.types";
import { defaultCity } from "@constants/cities";
import { fetchDynamicConfig } from "@services/booking-serivce";
import Image from "@components/atoms/Image";

export default function OnRoadPriceSection({
  slug,
  city,
  isLoggedIn,
  isSellable,
  pageType,
  carAvailability,
  modelData,
  brandName,
  fuelType,
}: {
  slug: string;
  city: City;
  isLoggedIn?: boolean;
  isSellable: boolean;
  pageType?: PageType;
  carAvailability?: string;
  modelData: any;
  brandName?: string;
  fuelType?: string;
}) {
  const router = useRouter();
  const { setCity: setContextCity, city: contextCity } = useCity();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [cityName, setCityName] = useState(city.name || defaultCity.name);
  const [changed, setIsChanging] = useState(false);
  
  // GST Banner Configuration
  const [gstConfig, setGstConfig] = useState<any>(null);
  const [shouldShowGST, setShouldShowGST] = useState(false);
  const [isEV, setIsEV] = useState(false);

  useEffect(() => {
    setContextCity({ ...city });
    setCityName(city?.name || defaultCity.name);
  }, [city]);

  useEffect(() => {
    if (contextCity.name !== city?.name && changed) {
      const citySlug = contextCity.city_slug
        ? contextCity.city_slug
        : contextCity?.name?.toLowerCase();
      router.push(`/cars/${slug}/price-in-${citySlug}/`);
      setIsChanging(false);
    }
  }, [contextCity]);

  // Fetch GST Banner Configuration
  useEffect(() => {
    const fetchGSTConfig = async () => {
      try {
        console.log("🔍 [OnRoadPriceSection] Fetching GST config...");
        const config = await fetchDynamicConfig({
          key: "new_car_gst",
          group: "Acko_Drive",
        });
        setGstConfig(config);
      } catch (error) {
        console.error("❌ [OnRoadPriceSection] Error fetching GST config:", error);
        setGstConfig(null);
      }
    };

    fetchGSTConfig();
  }, []);

  // Check if GST banner should be shown
  useEffect(() => {
    // Don't show banner if not sellable
    if (!isSellable) {
      setShouldShowGST(false);
      return;
    }

    const currentBrandName = brandName || modelData?.brand?.name;
    if (!currentBrandName || !cityName) {
      setShouldShowGST(false);
      return;
    }

    // Check if it's an EV car based on fuel type
    const normalizedBrand = currentBrandName?.toLowerCase?.()?.replace(/\s+/g, '-');
    const currentFuelType = fuelType || modelData?.fuelTypes?.[0];
    const isEVCar = currentFuelType?.toLowerCase() === 'electric';

    // Set EV state
    setIsEV(isEVCar);

    // For EV cars, show banner with EV text
    if (isEVCar) {
      setShouldShowGST(true);
      return;
    }

    // For non-EV cars, check dynamic config
    if (!gstConfig?.data?.new_car_gst?.brands) {
      setShouldShowGST(false);
      return;
    }

    const normalizedCity = cityName?.toLowerCase?.()?.replace(/\s+/g, '-');    
    const brandConfig = gstConfig?.data?.new_car_gst?.brands?.[normalizedBrand];    
    const shouldShow = brandConfig?.cities?.includes(normalizedCity) || false;    
    setShouldShowGST(shouldShow);
  }, [gstConfig, brandName, modelData?.brand?.name, cityName, isSellable, fuelType, modelData?.fuelTypes]);

  const clickHandler = async () => {
    const mmvDetails = {
      make: modelData?.brand?.name || "",
      model: modelData?.model?.name || "",
      variant: "",
      color: "",
    };
    await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      pageType: pageType,
      additionalProps: {
        action_type: "tap_btn",
        action_name: "tap_btn_login_to_save",
        section: "Summary",
        variant_name: "",
        car_availability: carAvailability || "",
        vehicle_make: modelData?.brand?.name || "",
        vehicle_model: modelData?.model?.name || "",
        city_name: cityName,
      },
    });
    if (typeof window !== "undefined") {
      sessionStorage.setItem("loginType", "Reveal price");
      localStorage.setItem(
        "MMV",
        JSON.stringify({
          make: mmvDetails?.make || "",
          model: mmvDetails?.model || "",
          variant: mmvDetails?.variant || "",
          color: mmvDetails?.color || "",
        })
      );
    }
    window.location.href = getLoginUrl(
      addCaptureIntentIfNeeded(
        window.location.href,
        mmvDetails,
        "Reveal price",
        pageType
      )
    );
  };

  const handleCitySelection = (city: City) => {
    const citySlug = city.city_slug
      ? city.city_slug
      : city?.name?.toLowerCase();
    setIsChanging(true);
    router.push(`/cars/${slug}/price-in-${citySlug}/`);
  };

  return (
    <div className={styles.wrapper}>
      <CitySelectionModal
        selectedCity={city}
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        handleCitySelection={handleCitySelection}
      />
      <div className={styles.heading} onClick={() => setIsModalOpen(true)}>
        <TitleWithIcon
          title={`${modelData?.brand.name} ${modelData?.model.name} On-Road Price in ${cityName}`}
          iconSrc={`${assetLoader()}/icons/Edit_pencil_change.png`}
          alt="Edit icon"
        />
      </div>

      <div className={styles.priceContainer}>
        <Text level="p" className={styles.price}>
          {`${getCurrencyWithLakh(
            modelData?.minPrice ?? 0
          )} – ${getCurrencyWithLakh(modelData?.maxPrice ?? 0)}`}
        </Text>
        {isSellable && (
          <Text level="p" className={styles.savings}>
            Save up to{" "}
            {isLoggedIn
              ? formatCurrencyINR(modelData?.minAckodriveSavings ?? 0)
              : maskAmount(modelData?.minAckodriveSavings ?? 0)}
          </Text>
        )}
        {shouldShowGST && (
          <div className={styles.gstBenefitCard}>
            <div className={styles.gstIcon}>
              <Image
                src={`${assetLoader()}/icons/new_car_gst_price.svg`}
                alt="GST Benefit"
                width={52}
                height={40}
              />
            </div>
            <div className={styles.gstText}>
              {isEV 
                ? "No GST impact on EV cars, prices are the same before and after."
                : "Good news! The price you see already includes GST savings."
              }
            </div>
          </div>
        )}
      </div>
      {isSellable && (
        <>
          {!isLoggedIn && (
            <Button
              className={styles.button}
              ariaLabel="Check ACKO Drive price"
              onClick={clickHandler}
            >
              {`Check ACKO Drive price`}
            </Button>
          )}
        </>
      )}
      {!isSellable && (
        <Text level="p" className={styles.unavailable}>
          {`**All the details are based on speculation.`}
        </Text>
      )}
    </div>
  );
}
